import React, { useEffect, useMemo, useState } from "react";
import { Lock, Check, X, Clock, RefreshCw, User } from "lucide-react";
import {
  listPrivateGalleryRequests,
  respondPrivateGalleryRequest,
  revokePrivateGalleryAccess,
} from "../../api/privateGallery";

const Badge = ({ status }) => {
  const styles = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    denied: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  };
  const icons = {
    pending: <Clock className="w-3 h-3" />,
    approved: <Check className="w-3 h-3" />,
    denied: <X className="w-3 h-3" />,
  };
  return (
    <span className={`text-xs px-2 py-1 rounded-full border flex items-center gap-1 ${styles[status] || styles.pending}`}>
      {icons[status]}
      <span className="capitalize">{status}</span>
    </span>
  );
};

const Row = ({ title, subtitle, right }) => (
  <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700">
    <div className="min-w-0 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
        <User className="w-5 h-5 text-slate-400" />
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-100 truncate">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</div>}
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0">{right}</div>
  </div>
);

export default function PrivateGalleryManage() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ pending: [], approved: [], denied: [] });
  const [activeTab, setActiveTab] = useState("pending");
  const [actionLoading, setActionLoading] = useState(null);

  const refresh = async () => {
    setErr("");
    setLoading(true);
    try {
      const res = await listPrivateGalleryRequests();
      setData({
        pending: res?.pending || [],
        approved: res?.approved || [],
        denied: res?.denied || [],
      });
    } catch (e) {
      setErr("Couldn&apos;t load private gallery requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const items = useMemo(() => data?.[activeTab] || [], [data, activeTab]);

  const approve = async (viewerUserId) => {
    setActionLoading(viewerUserId);
    try {
      await respondPrivateGalleryRequest(viewerUserId, "approve");
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const deny = async (viewerUserId) => {
    setActionLoading(viewerUserId);
    try {
      await respondPrivateGalleryRequest(viewerUserId, "deny");
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const revoke = async (viewerUserId) => {
    setActionLoading(viewerUserId);
    try {
      await revokePrivateGalleryAccess(viewerUserId);
      await refresh();
    } finally {
      setActionLoading(null);
    }
  };

  const tabs = [
    { key: "pending", label: `Pending (${data.pending?.length || 0})` },
    { key: "approved", label: `Approved (${data.approved?.length || 0})` },
    { key: "denied", label: `Denied (${data.denied?.length || 0})` },
  ];

  return (
    <div className="mt-6 rounded-2xl bg-slate-900/60 border border-slate-700 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-rose-400" />
          <div>
            <div className="text-base font-bold text-slate-100">Private Gallery</div>
            <div className="text-sm text-slate-500 mt-0.5">
              Consent-only. You decide who sees your private photos &amp; video.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-600 text-sm text-slate-300 flex items-center gap-1.5 transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="flex gap-2 mt-4">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`px-3 py-2 rounded-xl text-sm border transition ${
              activeTab === key
                ? "bg-rose-500/20 border-rose-400/30 text-rose-300"
                : "bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-slate-500">Loading…</div>
      ) : err ? (
        <div className="mt-4 text-sm text-rose-400">{err}</div>
      ) : items.length === 0 ? (
        <div className="mt-4 text-sm text-slate-500">
          {activeTab === "pending" ? "No requests yet." : "Nothing here yet."}
        </div>
      ) : (
        <div className="mt-4 grid gap-2">
          {items.map((x) => {
            const viewer = x?.viewer_user || {};
            const viewerId = x?.viewer_id || viewer?.id;
            const title = viewer?.display_name || viewerId || "Unknown user";
            const subtitle = x?.created_at
              ? `Requested: ${new Date(x.created_at).toLocaleDateString()}`
              : x?.source === "owner_grant"
              ? "Granted by you"
              : null;
            const isLoading = actionLoading === viewerId;

            return (
              <Row
                key={x?.id || viewerId}
                title={title}
                subtitle={subtitle}
                right={
                  activeTab === "pending" ? (
                    <>
                      <Badge status="pending" />
                      <button
                        type="button"
                        onClick={() => approve(viewerId)}
                        disabled={isLoading}
                        className="px-3 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-400/30 text-sm text-emerald-300 disabled:opacity-50 transition"
                      >
                        {isLoading ? "…" : "Approve"}
                      </button>
                      <button
                        type="button"
                        onClick={() => deny(viewerId)}
                        disabled={isLoading}
                        className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-600 text-sm text-slate-300 disabled:opacity-50 transition"
                      >
                        Deny
                      </button>
                    </>
                  ) : activeTab === "approved" ? (
                    <>
                      <Badge status="approved" />
                      <button
                        type="button"
                        onClick={() => revoke(viewerId)}
                        disabled={isLoading}
                        className="px-3 py-2 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/30 text-sm text-rose-300 disabled:opacity-50 transition"
                      >
                        {isLoading ? "…" : "Revoke"}
                      </button>
                    </>
                  ) : (
                    <Badge status="denied" />
                  )
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
